import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { UsersThree, Plus, Globe, LockSimple, SpinnerGap, ImageSquare, Images } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/PageHero";
import ShareButton from "@/components/ShareButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

function Avatars({ members }) {
  return (
    <div className="flex -space-x-2">
      {members.slice(0, 4).map((m, i) => (
        <span key={i} title={m.name} className="grid h-7 w-7 place-items-center rounded-full bg-[#F27A54] text-[10px] font-bold text-white ring-2 ring-white">
          {(m.name || "?").slice(0, 1).toUpperCase()}
        </span>
      ))}
      {members.length > 4 && <span className="grid h-7 w-7 place-items-center rounded-full bg-[#2D6A4F] text-[9px] font-bold text-white ring-2 ring-white">+{members.length - 4}</span>}
    </div>
  );
}

function AlbumCard({ a }) {
  return (
    <div className="relative">
      <Link to={`/albums/${a.id}`} data-testid={`album-card-${a.id}`}
        className="group block overflow-hidden rounded-3xl border border-[#E5DFD3] bg-white shadow-[0_8px_30px_rgba(44,39,36,0.05)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_16px_44px_rgba(44,39,36,0.12)]">
        <div className="relative aspect-[16/10] overflow-hidden">
          {a.cover_url ? (
            <img src={a.cover_url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="brand-sunset h-full w-full" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
          <span className={`absolute right-3 top-3 flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${a.visibility === "public" ? "bg-[#2D6A4F] text-white" : "bg-white/85 text-[#2C2724]"}`}>
            {a.visibility === "public" ? <Globe size={11} weight="fill" /> : <LockSimple size={11} weight="fill" />}{a.visibility === "public" ? "Public" : "Members"}
          </span>
          <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
            <h3 className="font-display text-lg font-bold text-white drop-shadow">{a.title}</h3>
          </div>
        </div>
        <div className="flex items-center justify-between p-4">
          <Avatars members={a.members} />
          <div className="flex items-center gap-3 text-xs font-semibold text-[#787167]">
            <span className="flex items-center gap-1"><Images size={14} weight="fill" />{a.photo_count ?? 0}</span>
            <span className="flex items-center gap-1"><UsersThree size={14} weight="fill" />{a.member_count}</span>
          </div>
        </div>
      </Link>
      {/* Share sits outside the overflow-hidden Link so its dropdown isn't clipped. */}
      <div className="absolute left-3 top-3 z-20">
        <ShareButton id={a.id} type="album" title={a.title} />
      </div>
    </div>
  );
}

export default function SharedAlbums() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("owned");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/shared-albums").then((r) => setData(r.data)).catch(() => setData({ owned: [], shared: [] }));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim()) { toast.error("Give your album a name"); return; }
    setBusy(true);
    try {
      await api.post("/shared-albums", { title: title.trim(), description: desc.trim() });
      toast.success("Album created");
      setOpen(false); setTitle(""); setDesc(""); load();
    } catch (e) { toast.error("Could not create album"); }
    setBusy(false);
  };

  const list = data ? (tab === "owned" ? data.owned : data.shared) : [];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHero
        seed="shared-albums" icon={UsersThree}
        eyebrow="Shared Albums"
        title="Albums with people you love"
        subtitle="Invite family & friends to view and add their own photos & videos — like a shared living gallery."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-album-btn" className="h-11 gap-1.5 rounded-full bg-white px-5 text-[#2C2724] hover:bg-white/90">
                <Plus size={17} weight="bold" /> New album
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-display text-xl">Create a shared album</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <input data-testid="album-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Kerala Trip 2027"
                  className="h-11 w-full rounded-xl border border-[#E5DFD3] px-3.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
                <textarea data-testid="album-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="What's this album about?"
                  className="w-full rounded-xl border border-[#E5DFD3] px-3.5 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[#F27A54]" />
                <Button data-testid="album-create-submit" onClick={create} disabled={busy} className="h-11 w-full rounded-full bg-[#F27A54] text-white hover:bg-[#d9633f]">
                  {busy ? <SpinnerGap size={16} className="animate-spin" /> : "Create album"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mt-6 flex gap-2">
        {[["owned", "My albums", data?.owned?.length], ["shared", "Shared with me", data?.shared?.length]].map(([k, l, c]) => (
          <button key={k} data-testid={`album-tab-${k}`} onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === k ? "bg-[#2C2724] text-white" : "bg-[#F0EBE1] text-[#5C554D] hover:bg-[#E5DFD3]"}`}>
            {l}{c != null ? ` (${c})` : ""}
          </button>
        ))}
      </div>

      {data === null ? (
        <div className="mt-16 grid place-items-center"><SpinnerGap size={30} className="animate-spin text-[#F27A54]" /></div>
      ) : list.length === 0 ? (
        <div className="mt-14 grid place-items-center rounded-3xl border border-dashed border-[#E5DFD3] p-12 text-center">
          <UsersThree size={44} weight="thin" className="text-[#D9C9B3]" />
          <p className="mt-3 font-display text-lg font-bold">{tab === "owned" ? "No albums yet" : "Nothing shared with you yet"}</p>
          <p className="text-sm text-[#787167]">{tab === "owned" ? "Create one and invite your people." : "When someone invites you, it'll appear here."}</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => <AlbumCard key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}
